/* eslint-disable no-param-reassign */
import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Icon } from "@mui/material";
import { toast } from "react-toastify";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DataTable from "components/Tables/DataTable";
import { useDispatch, useSelector } from "react-redux";

import { actions } from "store/reducers/users";
import { actions as numbersActions } from "store/reducers/numbers";
import userHelpers from "./data/users";
import Modal from "./modal";

function Users() {
  const dispatch = useDispatch();

  const { getRows, columns } = userHelpers();
  const [userToEdit, setUserToEdit] = useState(null);

  const { users, totalItems, page, perPage, qs } = useSelector((state) => state.users);

  const payGroups = useSelector((state) => state.numbers.payGroups);
  const company = useSelector((state) => state.authentication?.user?.company);

  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const handleClose = useCallback(() => {
    setOpen(false);
    setUserToEdit(null);
  }, [setOpen, setUserToEdit]);

  const rows = useMemo(
    () =>
      getRows(
        users,
        setUserToEdit,
        payGroups.reduce((all, l) => {
          all[l.id] = l;
          return all;
        }, {})
      ),
    [users, payGroups, setUserToEdit]
  );

  const submit = useCallback(
    (data, call) => {
      if (company) {
        if (userToEdit) {
          const formattedData = {
            ...userToEdit,
            ...data,
            company: company.id,
          };
          if (userToEdit.permissions) {
            formattedData.permissions = {
              ...userToEdit.permissions,
              payGroups: data.payGroups,
            };
          } else {
            formattedData.permissions = {
              payGroups: data.payGroups,
            };
          }
          dispatch(
            actions.updateUser(
              userToEdit.id,
              formattedData,
              () => {
                toast.success("User updated successfully");
                handleClose();
              },
              (msg, error) => {
                toast.error(msg || "User could not be updated");
                call(error);
              }
            )
          );
        } else {
          const formattedData = {
            ...data,
            permissions: {
              payGroups: data.payGroups,
            },
            company: company.id,
          };
          dispatch(
            actions.addUser(
              formattedData,
              () => {
                toast.success("User created successfully");
                handleClose();
              },
              (msg, error) => {
                toast.error(msg || "User could not be created");
                call(error);
              }
            )
          );
        }
      }
    },
    [userToEdit, dispatch, handleClose, company]
  );

  useEffect(() => {
    dispatch(numbersActions.fetchPayGroupsRequest((err) => toast.error(err)));
  }, [dispatch]);

  useEffect(() => {
    dispatch(actions.fetchUserRequest(page, perPage, (err) => toast.error(err)));
  }, [page, perPage, qs]);

  return (
    <Card id="users">
      <MDBox pt={3}>
        <MDBox pl={3}>
          <MDButton
            variant="gradient"
            color="primary"
            size="medium"
            aria-label="close"
            onClick={handleOpen}
          >
            <Icon fontSize="medium">add</Icon> Add User
          </MDButton>
        </MDBox>

        <DataTable
          entriesPerPage={{ defaultValue: perPage, entries: [5, 10, 15, 20, 50] }}
          table={{ columns, rows }}
          isSorted={false}
          showTotalEntries
          noEndBorder
          canSearch
          totalCount={totalItems}
          page={page}
          perPage={perPage}
          qs={qs}
          setQs={(s) => dispatch(actions.setUserQs(s))}
          gotoPage={(p) => dispatch(actions.setUserPage(p))}
          setPageSize={(p) => dispatch(actions.setUserPerPage(p))}
        />
      </MDBox>
      <Modal
        open={!!(open || userToEdit)}
        handleClose={handleClose}
        onSumbit={submit}
        data={userToEdit}
        isEdit={userToEdit !== null}
        payGroups={payGroups}
      />
    </Card>
  );
}

Users.propTypes = {};

export default Users;
